<template>
  <AvsTabs
    v-model="tab"
    :loading="loading"
  >
    <template v-slot:tabs>
      <v-tab>
        <v-icon left>mdi-magnify-scan</v-icon>
        {{ $t("commons.informations") }}
      </v-tab>
      <v-tab :disabled="!exists()">
        <v-icon left>mdi-clipboard-text-outline</v-icon>
        {{ $t("commons.form") }}
      </v-tab>
      <v-tab :disabled="!exists()">
        <v-icon left>mdi-alert-outline</v-icon>
        {{ $t("commons.signatures") }}
      </v-tab>
    </template>

    <template v-slot:items>
      <v-tab-item key="0">
        <DocumentForm
          v-model="document"
          :fields="fields"
          :isEditable="isEditable"
          :performSave="performDocumentSave"
          @success="v => handleSuccess(v, 1)"
          @error="handleError"
        />
      </v-tab-item>
      <v-tab-item key="1">
        <DocumentValuesForm
          :documentId="document.id"
          :template="template"
          :values="values"
          :isEditable="isEditable"
          @success="v => handleSuccess(v, 2, loadDataForm)"
          @error="handleError"
        />
      </v-tab-item>
      <v-tab-item key="2">
        <DocumentSign
          :document="document"
          :signatures="signatures"
          @input="handleInput"
          @success="(v, m) => handleSuccess(v, 2, loadSignForm, m)"
          @error="handleError"
        />
      </v-tab-item>
    </template>
  </AvsTabs>
</template>

<script>
import { DocumentService, TemplateService } from "@/services/documents"
import { STATE_ENUM } from "@/utils/consts"

const noop = () => undefined

export default {
  name: "DocumentEditor",
  components: {
    AvsTabs: () => import("@/components/generics/general/AvsTabs.vue"),
    DocumentForm: () => import("@/components/ri3/documents/DocumentForm.vue"),
    DocumentSign: () => import("@/components/ri3/documents/DocumentSign.vue"),
    DocumentValuesForm: () => import("@/components/ri3/documents/DocumentValuesForm.vue")
  },
  props: {
    value: {
      type: Object,
      required: true
    },
    fields: {
      type: Object,
      default: () => ({
        name: true,
        project: true,
        template: true
      })
    },
    performDocumentSave: {
      type: Function,
      required: true
    }
  },
  computed: {
    isEditable() {
      return this.document.state.id === STATE_ENUM.EDITABLE.id
    },
    document: {
      get: function () {
        return Object.assign({}, this.value)
      },
      set: function (newValue) {
        this.$emit("input", newValue)
      }
    }
  },
  data() {
    return {
      template: null,
      values: null,
      signatures: undefined,
      loading: false,
      tab: null
    }
  },
  methods: {
    exists() {
      return this.document?.id > 0
    },
    async fetchTemplateDetails() {
      this.template = await TemplateService.details.get(this.document.template.id)
    },
    async fetchDocumentValues() {
      this.values = await DocumentService.values.getAll(this.document.id)
    },
    async fetchDocumentSignatures() {
      this.signatures = await DocumentService.signatures.getAll(this.document.id)
    },
    async loadDataForm() {
      if (this.exists()) {
        this.loading = true
        const promises = [this.fetchDocumentValues()]
        if (!this.template) promises.push(this.fetchTemplateDetails())
        Promise.all(promises).finally(() => {
          this.loading = false
        })
      }
    },
    async loadSignForm() {
      if (this.exists()) {
        this.loading = true
        this.fetchDocumentSignatures().finally(() => {
          this.loading = false
        })
      }
    },
    handleInput(newValue) {
      this.document = newValue
    },
    handleSignatureSuccess(updatedData) {
      // Mettez à jour le composant parent avec les données mises à jour
      this.document = updatedData
    },
    handleSuccess(isSaved, nextTab, postProcess = noop, message = null) {
      // Handle message display
      const msg_success = message ? message : this.$t("messages.success.operation")
      const data = isSaved
        ? { msg: msg_success, color: "success" }
        : { msg: this.$t("messages.success.no_updated"), color: "info" }
      this.$store.commit("app/message", data)

      // Handle next display
      this.tab = nextTab

      // Postprocess
      postProcess()
    },
    handleError(msg) {
      // Handle message display
      this.$store.commit("app/message", { msg, color: "error" })
    },
    next(nextTab) {
      this.tab = nextTab
    }
  },
  watch: {
    tab: {
      handler(newValue, oldValue) {
        if (newValue === 1) this.loadDataForm()
        else if (newValue === 2) this.loadSignForm()
      }
    }
  }
}
</script>
